import React, { Fragment, useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { toast } from "react-hot-toast";

import FormField from "@mhj/shared/FormField";
import { apiRequest } from "@mhj/shared/api";
import type { AuthUser, UserRole } from "@mhj/shared/types";

type AdminUser = AuthUser & { disabled?: boolean };

// super_admin is deliberately absent: promotion to it isn't possible from
// this UI, matching updateRoleSchema on the server (D12 item 5). The
// disabled state on the update button already guards editing a user who
// *is* one; this guards against creating a new one from here.
const ROLE_OPTIONS: UserRole[] = [
  "admin",
  "content_editor",
  "scholarship_reviewer",
  "empowerment_reviewer",
  "field_officer",
  "user"
];

const CREATE_ROLE_OPTIONS: Array<
  Extract<UserRole, "admin" | "content_editor" | "scholarship_reviewer" | "empowerment_reviewer" | "field_officer">
> = ["admin", "content_editor", "scholarship_reviewer", "empowerment_reviewer", "field_officer"];

type CreateAdminForm = {
  fullName: string;
  email: string;
  phone: string;
  role: Extract<
    UserRole,
    "admin" | "content_editor" | "scholarship_reviewer" | "empowerment_reviewer" | "field_officer"
  >;
};

export default function AdminUsers() {
  const queryClient = useQueryClient();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [selected, setSelected] = useState<AdminUser | null>(null);
  const { register: roleRegister, setValue, handleSubmit: handleRoleSubmit } = useForm<{
    role: UserRole;
  }>({
    defaultValues: { role: "user" },
  });
  const {
    register: createRegister,
    handleSubmit: handleCreateSubmit,
    reset: resetCreateForm,
    formState: { errors: createErrors },
  } = useForm<CreateAdminForm>({
    defaultValues: {
      fullName: "",
      email: "",
      phone: "",
      role: "admin",
    },
  });
  const { data, error } = useQuery({
    queryKey: ["admin", "users"],
    queryFn: () => apiRequest<{ users: AdminUser[] }>("/admin/users"),
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
  });

  useEffect(() => {
    if (data?.users) {
      setUsers(data.users);
    }
  }, [data]);

  useEffect(() => {
    if (error) {
      toast.error((error as Error).message);
    }
  }, [error]);

  const roleMutation = useMutation({
    mutationFn: (payload: { id: string; role: UserRole }) =>
      apiRequest(`/admin/users/${payload.id}/role`, {
        method: "POST",
        data: { role: payload.role },
        withCsrf: true,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
      setSelected(null);
      toast.success("User role updated.");
    },
  });

  const createAdminMutation = useMutation({
    mutationFn: (payload: CreateAdminForm) =>
      apiRequest<{ user: AdminUser }>("/admin/users", {
        method: "POST",
        data: {
          fullName: payload.fullName.trim(),
          email: payload.email.trim(),
          phone: payload.phone.trim() || null,
          role: payload.role,
        },
        withCsrf: true,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
      resetCreateForm();
      toast.success("Admin invite sent.");
    },
  });

  const disableMutation = useMutation({
    mutationFn: (id: string) =>
      apiRequest(`/admin/users/${id}/disable`, {
        method: "POST",
        withCsrf: true,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
      setSelected(null);
      toast.success("User disabled.");
    },
  });

  const handleSelect = (user: AdminUser) => {
    setSelected(user);
    setValue("role", user.role);
  };

  const handleRole = async (values: { role: UserRole }) => {
    if (!selected) return;
    try {
      await roleMutation.mutateAsync({ id: selected.id, role: values.role });
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  const handleCreateAdmin = async (values: CreateAdminForm) => {
    try {
      await createAdminMutation.mutateAsync(values);
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  const handleDisable = async () => {
    if (!selected) return;
    try {
      await disableMutation.mutateAsync(selected.id);
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_1fr]">
      <div className="glass-card rounded-3xl p-6">
        <div className="flex items-center justify-between">
          <p className="text-xs uppercase tracking-[0.3em] text-blue-300">
            Admin users
          </p>
          <button
            onClick={() => setSelected(null)}
            className="rounded-full bg-blue-500 px-5 py-2 text-xs uppercase tracking-[0.2em] text-white transition hover:bg-blue-400 disabled:cursor-not-allowed disabled:opacity-60"
          >
            Add admin
          </button>
        </div>
        <div className="mt-6 space-y-3 text-sm text-black/70">
          {users.length === 0 ? (
            <p>No admins yet.</p>
          ) : (
            users.map((user) => (
              <button
                key={user.id}
                onClick={() => handleSelect(user)}
                className="w-full rounded-2xl border border-black/10 bg-white/60 px-4 py-3 text-left transition hover:border-blue-400"
              >
                <p className="font-semibold text-black">{user.fullName}</p>
                <p className="text-xs text-black/50">
                  {user.email} · {user.role}
                </p>
              </button>
            ))
          )}
        </div>
      </div>
      <div className="glass-card rounded-3xl p-6">
        {selected == null ? (<Fragment>
          <p className="text-xs uppercase tracking-[0.3em] text-blue-300">
            Add admin
          </p>
          <form onSubmit={handleCreateSubmit(handleCreateAdmin)} className="mt-4 space-y-4">
            <FormField label="Full name">
              <input
                type="text"
                {...createRegister("fullName", { required: "Full name is required" })}
                className="rounded-2xl border border-black/10 bg-white/60 px-4 py-3 text-black focus:border-blue-400 focus:outline-none"
                placeholder="Admin name"
              />
            </FormField>
            {createErrors.fullName ? (
              <p className="text-xs text-red-500">{createErrors.fullName.message}</p>
            ) : null}
            <FormField label="Email">
              <input
                type="email"
                {...createRegister("email", { required: "Email is required" })}
                className="rounded-2xl border border-black/10 bg-white/60 px-4 py-3 text-black focus:border-blue-400 focus:outline-none"
                placeholder="admin@example.com"
              />
            </FormField>
            {createErrors.email ? (
              <p className="text-xs text-red-500">{createErrors.email.message}</p>
            ) : null}
            <div className="grid gap-4 md:grid-cols-2">
              <FormField label="Phone (optional)">
                <input
                  type="tel"
                  {...createRegister("phone")}
                  className="rounded-2xl border border-black/10 bg-white/60 px-4 py-3 text-black focus:border-blue-400 focus:outline-none"
                  placeholder="+234..."
                />
              </FormField>
              <FormField label="Role">
                <select
                  {...createRegister("role")}
                  className="rounded-2xl border border-black/10 bg-white/60 px-4 py-3 text-black focus:border-blue-400 focus:outline-none"
                >
                  {CREATE_ROLE_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option.replace(/_/g, " ")}
                    </option>
                  ))}
                </select>
              </FormField>
            </div>
            <p className="text-xs text-black/60">
              We will email this admin an activation link to set their password and activate the account.
            </p>
            <button
              type="submit"
              disabled={createAdminMutation.isPending}
              className="rounded-full bg-blue-500 px-5 py-2 text-xs uppercase tracking-[0.2em] text-white transition hover:bg-blue-400 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {createAdminMutation.isPending ? "Adding..." : "Add admin"}
            </button>
          </form>
          <div className="my-6 h-px w-full bg-black/10" />
        </Fragment>)
:
        (<Fragment>
          <p className="text-xs uppercase tracking-[0.3em] text-blue-300">
            Role management
          </p>
          {selected ? (
            <>
              <h3 className="mt-3 text-xl font-semibold text-black">
                {selected.fullName}
              </h3>
              <p className="mt-1 text-xs text-black/50">{selected.email}</p>
              <div className="mt-6">
                <FormField label="Role">
                  <select
                    {...roleRegister("role")}
                    className="rounded-2xl border border-black/10 bg-white/60 px-4 py-3 text-black focus:border-blue-400 focus:outline-none"
                  >
                    {ROLE_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option.replace(/_/g, " ")}
                      </option>
                    ))}
                  </select>
                </FormField>
              </div>
              <div className="mt-6 flex flex-wrap gap-3">
                <button
                  onClick={handleRoleSubmit(handleRole)}
                  disabled={roleMutation.isPending || selected.role === "super_admin"}
                  className="rounded-full bg-blue-500 px-5 py-2 text-xs uppercase tracking-[0.2em] text-black transition hover:bg-blue-400 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Update role
                </button>
                <button
                  onClick={() => void handleDisable()}
                  disabled={disableMutation.isPending || selected.role === "super_admin"}
                  className="rounded-full border border-red-500/40 px-5 py-2 text-xs uppercase tracking-[0.2em] text-red-500 transition hover:border-red-400 hover:text-red-100 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Disable admin
                </button>
              </div>
            </>
          ) : (
            <p className="mt-4 text-sm text-black/60">
              Select an admin to update role.
            </p>
          )}
        </Fragment>)}
      </div>
    </div>
  );
}
